import React, { useState, useEffect, useCallback } from 'react';
import { Radio, RadioGroup } from '@ygtechnologies/rubus-ui';
import { useUniqueId, useAsyncState } from '@/utils/hooks';
import { TranslatableText, DocumentStatus, ResidentialUnitExtraData } from '@/types';
import { getDocumentLinkedResidentialUnits } from '@/services/documents';
import { UnitSelector } from '../UnitSelector';

interface DocumentResidentialUnitSelectionProps {
    projectId: number;
    documentVersionId: number;
    residentialUnitId: number | null;

    // value: number[];
    onChange(residentialUnitIds: number[]): void;

    singleCaption: TranslatableText;
    allCaption: TranslatableText;
    multiCaption: TranslatableText;
}

const DocumentResidentialUnitSelection: React.FC<DocumentResidentialUnitSelectionProps> = ({
    projectId,
    documentVersionId,
    residentialUnitId,

    onChange,

    singleCaption,
    allCaption,
    multiCaption,
}) => {
    const [mode, setMode] = useState<'Unit' | 'Selection' | 'All'>('Unit');
    const radioName = useUniqueId();
    const [links] = useAsyncState(() => getDocumentLinkedResidentialUnits(documentVersionId), [documentVersionId], null);
    const [selectedResidentialUnitIds, setSelectedResidentialUnitIds] = useState<number[]>([]);

    const [linkedResidentialUnitIds, setLinkedResidentialUnitIds] = useState<Set<number> | null>(null);

    useEffect(() => {
        if (mode === 'Unit' && links && !residentialUnitId) {
            setMode('All');
        }
    }, [residentialUnitId, mode, links]);

    useEffect(() => {
        if (links) {
            setLinkedResidentialUnitIds(
                new Set(links.filter((x) => x.status === DocumentStatus.Pending).map((x) => x.residentialUnitId))
            );
        } else {
            setLinkedResidentialUnitIds(null);
        }
    }, [links]);

    const unitFilter = useCallback(
        (residentialUnit: ResidentialUnitExtraData) => linkedResidentialUnitIds!.has(residentialUnit.id),
        [linkedResidentialUnitIds]
    );

    useEffect(() => {
        let selection: number[] = [];

        if (mode === 'Unit') {
            selection = [residentialUnitId!];
        } else if (mode === 'All') {
            selection = links!.filter((x) => x.status === DocumentStatus.Pending).map((x) => x.residentialUnitId);
        } else if (mode === 'Selection') {
            selection = selectedResidentialUnitIds;
        }

        onChange(selection);
    }, [links, mode, residentialUnitId, selectedResidentialUnitIds, onChange]);

    if (!linkedResidentialUnitIds || !links) {
        return null;
    }

    return (
        <>
            <RadioGroup>
                {residentialUnitId && (
                    <Radio inputSize="md" name={radioName} checked={mode === 'Unit'} onChange={() => setMode('Unit')}>
                        {singleCaption}
                    </Radio>
                )}

                <Radio
                    inputSize="md"
                    name={radioName}
                    checked={mode === 'All'}
                    onChange={() => setMode('All')}
                    disabled={!!residentialUnitId && links.length === 1}>
                    {allCaption}
                </Radio>

                <Radio
                    inputSize="md"
                    name={radioName}
                    checked={mode === 'Selection'}
                    onChange={() => setMode('Selection')}
                    disabled={!!residentialUnitId && links.length === 1}>
                    {multiCaption}
                </Radio>
            </RadioGroup>
            {mode === 'Selection' && linkedResidentialUnitIds && (
                <UnitSelector
                    projectId={projectId}
                    selectedResidentialUnitIds={selectedResidentialUnitIds}
                    filter={unitFilter}
                    onChange={setSelectedResidentialUnitIds}
                />
            )}
        </>
    );
};

export default DocumentResidentialUnitSelection;
